import React, { useEffect, useState } from "react";
import "../App.css";
import Navbar from "./Nav";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

import Part1 from "../media/part1.png";
import Part2 from "../media/part2.png";
import EyalIMG from "../media/eyal.png";
import CV from "../media/EyalCV.pdf";

import Cornerstone1 from "../media/Cornerstone1.png";
import Cornerstone2 from "../media/Cornerstone2.png";
import IMGProcessing1 from "../media/IMGProcessing1.png";
import IMGProcessing2 from "../media/IMGProcessing2.png";
import SpaceGame1 from "../media/space_game.png";
import SpaceGame2 from "../media/space_game2.png";
import Radiation1 from "../media/radiation1.png";
import Radiation2 from "../media/radiation2.png";
import TicTacToe1 from "../media/JavaTicTacToe1.png";
import TicTacToe2 from "../media/JavaTicTacToe2.png";
import Cloud1 from "../media/Cloud1.png";
import Cloud2 from "../media/Cloud2.png";

import Preview1 from "../media/preview1.jpg";
import Preview2 from "../media/preview2.jpg";
import Preview3 from "../media/preview3.jpg";
import Preview4 from "../media/preview4.jpg";

gsap.registerPlugin(ScrollTrigger);

const solaroShots = [
  { src: Preview1, label: "Workout Plan" },
  { src: Preview2, label: "AI Coach Check-ins" },
  { src: Preview4, label: "Progress & Analytics" },
  { src: Preview3, label: "Plan Management" },
];

const projects = [
  {
    title: 'Cornerstone — Capstone Platform',
    category: "University",
    award: "Best CS Capstone Project 2024 · University of Auckland",
    description:
      "A project management and allocation platform built for the Computer Science capstone course at the University of Auckland — solving a real problem for lecturers and students. I owned the backend: database design, API routes, query design, and user authentication with password hashing and Google OAuth.",
    tech: ["React", "Node.js", "MySQL", "AWS", "OAuth 2.0"],
    img: Cornerstone1,
    imgAlt: Cornerstone2,
  },
  {
    title: "Coin Detection — Image Processing",
    category: "University",
    description:
      "An image-processing pipeline that detects and counts coins in photographs. Filtering, adaptive thresholding, erosion and dilation — implemented from first principles to really understand what happens beneath the library calls.",
    tech: ["Python", "Image Processing"],
    img: IMGProcessing1,
    imgAlt: IMGProcessing2,
  },
  {
    title: "Space Game",
    category: "Game Development",
    description:
      "An action-packed, story-driven space shooter about unravelling an alien invasion of Earth. Combat, level progression and an increasingly powerful ship carry players through a journey across time and space.",
    tech: ["Unity", "C#", "Blender"],
    img: SpaceGame1,
    imgAlt: SpaceGame2,
  },
  {
    title: "Abandoned Nuclear Powerplant",
    category: "Game Development",
    description:
      "A story-driven horror game set in an abandoned nuclear power plant. Atmosphere-first design: explore the facility, uncover why it was abandoned, and capture evidence of the anomalies you find along the way.",
    tech: ["Unity", "C#"],
    img: Radiation2,
    imgAlt: Radiation1,
  },
  {
    title: "Java TicTacToe",
    category: "Software",
    award: "Wallace Cup & Book Award — first-equal in Digital Technology",
    description:
      "My first major project, built in high school — and the one that sparked my passion for programming and computer science in the first place.",
    tech: ["Java", "NetBeans"],
    img: TicTacToe1,
    imgAlt: TicTacToe2,
  },
  {
    title: "Cloud Backup Web Application",
    category: "Software",
    description:
      "A self-hosted alternative to services like Google Photos: data lives locally on my own hardware while staying remotely accessible, just like a traditional cloud. Whenever I travel, this server becomes my personal cloud storage.",
    tech: ["React", "HTML", "CSS"],
    img: Cloud1,
    imgAlt: Cloud2,
  },
];

const milestones = [
  {
    title: "Design",
    text: "Wireframes, user flows and a full design system — every screen thought through before a line of code.",
  },
  {
    title: "Build",
    text: "A clean, component-driven Flutter architecture with a fully themeable UI.",
  },
  {
    title: "Refine",
    text: "Real-device testing, performance passes and relentless polish until it felt production-grade.",
  },
  {
    title: "Ship",
    text: "Released and live in production — maintained and improved with every iteration.",
  },
];

const interests = [
  "Full-Stack Web Development",
  "Mobile App Development",
  "Computer Hardware",
  "UI/UX Design",
  "Game Development",
  "Integrating AI"
];

function Home() {
  // Solaro phone carousel
  const [shot, setShot] = useState(0);

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const id = setInterval(() => {
      setShot((s) => (s + 1) % solaroShots.length);
    }, 3200);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    // --- Smooth scrolling (Lenis) driven by GSAP's ticker ---
    let lenis;
    let tickerCallback;
    if (!reduceMotion) {
      lenis = new Lenis();
      lenis.on("scroll", ScrollTrigger.update);
      tickerCallback = (time) => lenis.raf(time * 1000);
      gsap.ticker.add(tickerCallback);
      gsap.ticker.lagSmoothing(0);
    }

    // --- Smooth anchor navigation (accounts for the sticky nav) ---
    const onAnchorClick = (e) => {
      const link = e.target.closest('a[href^="#"]');
      if (!link) return;
      const target = document.querySelector(link.getAttribute("href"));
      if (!target) return;
      e.preventDefault();
      if (lenis) {
        lenis.scrollTo(target, { offset: -72, duration: 1.4 });
      } else {
        target.scrollIntoView({ behavior: "smooth" });
      }
    };
    document.addEventListener("click", onAnchorClick);

    // Once every asset has loaded, re-measure the page so Lenis's scroll
    // limit and ScrollTrigger positions account for the full-size images.
    const onLoad = () => {
      if (lenis) lenis.resize();
      ScrollTrigger.refresh();
    };
    if (document.readyState === "complete") onLoad();
    else window.addEventListener("load", onLoad);

    const ctx = gsap.context(() => {
      if (reduceMotion) return;

      // Hero entrance
      gsap.from(".parallax__content > *", {
        y: 42,
        opacity: 0,
        duration: 0.9,
        stagger: 0.12,
        ease: "power3.out",
        delay: 0.15,
      });
      gsap.from(".hero__scrollCue", { opacity: 0, duration: 0.8, delay: 1.3 });

      // Hero parallax depth (scrubbed by scroll)
      document.querySelectorAll("[data-parallax-layers]").forEach((triggerElement) => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: triggerElement,
            start: "0% 0%",
            end: "100% 0%",
            scrub: 0,
          },
        });
        [
          { layer: "1", yPercent: 70 },
          { layer: "2", yPercent: 55 },
          { layer: "3", yPercent: 40 },
          { layer: "4", yPercent: 10 },
        ].forEach((cfg, i) => {
          tl.to(
            triggerElement.querySelectorAll(`[data-parallax-layer="${cfg.layer}"]`),
            { yPercent: cfg.yPercent, ease: "none" },
            i === 0 ? undefined : "<"
          );
        });
      });

      // Generic reveal-on-scroll
      gsap.utils.toArray(".reveal").forEach((el) => {
        gsap.from(el, {
          y: 46,
          opacity: 0,
          duration: 0.85,
          ease: "power3.out",
          scrollTrigger: { trigger: el, start: "top 86%" },
        });
      });

      // Project rows: image glides in from its side, text follows
      gsap.utils.toArray(".projectRow").forEach((row) => {
        const flipped = row.classList.contains("projectRow--flip");
        gsap.from(row.querySelector(".projectRow__media"), {
          x: flipped ? -64 : 64,
          opacity: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: { trigger: row, start: "top 78%" },
        });
        gsap.from(row.querySelectorAll(".projectRow__body > *"), {
          y: 34,
          opacity: 0,
          duration: 0.75,
          stagger: 0.09,
          ease: "power3.out",
          scrollTrigger: { trigger: row, start: "top 78%" },
        });
      });

      // Showcase milestones pop in one by one
      gsap.from(".milestone", {
        y: 30,
        opacity: 0,
        duration: 0.7,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: { trigger: ".milestones", start: "top 85%" },
      });

      // Phone gently floats
      gsap.to(".phoneMock", {
        y: -13,
        duration: 2.6,
        ease: "sine.inOut",
        yoyo: true,
        repeat: -1,
      });
    });

    return () => {
      window.removeEventListener("load", onLoad);
      document.removeEventListener("click", onAnchorClick);
      ctx.revert();
      if (tickerCallback) gsap.ticker.remove(tickerCallback);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <div className="App">
      {/* ---------------- Hero ---------------- */}
      <section className="landingPage" id="landingPage">
        <div className="parallax">
          <section className="parallax__header">
            <div className="parallax__visuals">
              <div data-parallax-layers className="parallax__layers">
                <img
                  src={Part2}
                  loading="eager"
                  data-parallax-layer="1"
                  alt=""
                  className="parallax__layer-img"
                />
                <img
                  src={Part1}
                  loading="eager"
                  data-parallax-layer="3"
                  alt=""
                  className="parallax__layer-img-person"
                />
                <div data-parallax-layer="2" className="parallax__content">
                  <h1 className="hero__title">Hi, I'm Eyal</h1>
                  <p className="hero__subtitle">Software Engineer · Full-Stack Developer</p>
                  <div className="hero__ctas">
                    <a className="btn btn--primary" href="#showcase">
                      View my work
                    </a>
                    <a className="btn btn--ghost" href="#contact">
                      Get in touch
                    </a>
                  </div>
                </div>
              </div>
              <div className="parallax__fade"></div>
            </div>
            <a className="hero__scrollCue" href="#about" aria-label="Scroll to the about section">
              <span></span>
            </a>
          </section>
        </div>
      </section>

      <Navbar />

      <main>
        {/* ---------------- About ---------------- */}
        <section className="section" id="about">
          <div className="container">
            <div className="sectionHead reveal">
              <span className="sectionHead__kicker">01 · About</span>
              <h2 className="sectionHead__title">A bit about me</h2>
            </div>
            <div className="about__grid">
              <div className="aboutCard reveal">
                <div className="aboutCard__head">
                  <img src={EyalIMG} className="aboutCard__photo" alt="Eyal Blumental Erez" />
                  <div>
                    <h3>Eyal Blumental Erez</h3>
                    <p className="aboutCard__loc">Auckland, New Zealand</p>
                  </div>
                </div>
                <p className="aboutCard__bio">
                  I believe in continuous learning and keeping an open mind to new ideas. I'm
                  always expanding my knowledge of technologies, frameworks and programming
                  languages, with a strong passion for software and full-stack web development.
                  Away from the computer you'll find me reading, powerlifting or snowboarding.
                </p>
              </div>
              <div className="about__side">
                <div className="sideBlock reveal">
                  <h3 className="sideBlock__title">Education</h3>
                  <div className="edu">
                    <div className="edu__item">
                      <div className="edu__degree">Bachelor of Science, Computer Science</div>
                      <div className="edu__school">University of Auckland</div>
                      <div className="edu__date">Graduated May 2025</div>
                    </div>
                    <div className="edu__item">
                      <div className="edu__degree">NCEA Level 3</div>
                      <div className="edu__detail">Excellence Endorsement — Digital Technology</div>
                      <div className="edu__detail">Merit Endorsement — Calculus · Overall Merit</div>
                    </div>
                  </div>
                </div>
                <div className="sideBlock reveal">
                  <h3 className="sideBlock__title">Interests</h3>
                  <div className="chips">
                    {interests.map((item) => (
                      <span className="chip" key={item}>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Solaro showcase ---------------- */}
        <section className="section showcase" id="showcase">
          <div className="container">
            <div className="showcase__grid">
              <div className="showcase__copy">
                <div className="sectionHead reveal">
                  <span className="sectionHead__kicker">02 · Featured build</span>
                  <h2 className="sectionHead__title">Solaro</h2>
                  <p className="showcase__tagline">
                    A fitness app - designed, built and shipped end-to-end, now live
                    in production.
                  </p>
                </div>
                <p className="showcase__desc reveal">
                  Solaro is the project that proves the full journey. I took it from first sketch
                  to production release single-handedly: product design, architecture,
                  implementation, AI Features, testing and release. Every screen, every decision, every line —
                  built to be used, not just to demo.
                </p>
                <div className="milestones">
                  {milestones.map((m) => (
                    <div className="milestone" key={m.title}>
                      <h4>{m.title}</h4>
                      <p>{m.text}</p>
                    </div>
                  ))}
                </div>
                <div className="chips reveal">
                  {["Flutter", "Dart", "C#/.NET", "PostgreSQL", "UI/UX Design"].map((t) => (
                    <span className="chip chip--amber" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
              <div className="showcase__visual reveal">
                <div className="phoneMock">
                  <div className="phoneScreen">
                    {solaroShots.map((s, i) => (
                      <img
                        key={s.label}
                        src={s.src}
                        alt={`Solaro — ${s.label}`}
                        className={`phoneScreen__img${i === shot ? " is-active" : ""}`}
                        loading="lazy"
                        decoding="async"
                        aria-hidden={i === shot ? undefined : "true"}
                      />
                    ))}
                  </div>
                </div>
                <div className="phoneDots" role="tablist" aria-label="Solaro screens">
                  {solaroShots.map((s, i) => (
                    <button
                      key={s.label}
                      type="button"
                      role="tab"
                      className={`phoneDot${i === shot ? " is-active" : ""}`}
                      aria-selected={i === shot}
                      aria-label={s.label}
                      onClick={() => setShot(i)}
                    />
                  ))}
                </div>
                <p className="showcase__caption">Solaro · {solaroShots[shot].label}</p>
              </div>
            </div>
          </div>
        </section>

        {/* ---------------- Projects ---------------- */}
        <section className="section section--surface" id="projects">
          <div className="container">
            <div className="sectionHead reveal">
              <span className="sectionHead__kicker">03 · Projects</span>
              <h2 className="sectionHead__title">Selected work</h2>
              <p className="sectionHead__sub">
                University builds, games and software — hover any screenshot for a second look.
              </p>
            </div>
            {projects.map((p, i) => (
              <article
                className={`projectRow${i % 2 === 1 ? " projectRow--flip" : ""}`}
                key={p.title}
              >
                <div className="projectRow__media">
                  <span className="projectRow__tag">{p.category}</span>
                  <img src={p.img} alt={p.title} loading="lazy" decoding="async" />
                  <img
                    src={p.imgAlt}
                    alt=""
                    aria-hidden="true"
                    className="alt"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <div className="projectRow__body">
                  <h3 className="projectRow__title">{p.title}</h3>
                  {p.award && <span className="awardBadge">🏆 {p.award}</span>}
                  <p className="projectRow__desc">{p.description}</p>
                  <div className="chips">
                    {p.tech.map((t) => (
                      <span className="chip" key={t}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* ---------------- Contact ---------------- */}
        <section className="section" id="contact">
          <div className="container">
            <div className="sectionHead reveal">
              <span className="sectionHead__kicker">04 · Contact</span>
              <h2 className="sectionHead__title">Let's build something</h2>
              <p className="sectionHead__sub">
                Open to software and full-stack opportunities — or just to talk shop. My inbox is
                always open.
              </p>
            </div>
            <div className="contact__grid reveal">
              <a className="contactCard" href="mailto:eyalglraz@gmail.com">
                <span className="contactCard__icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="5" width="18" height="14" rx="2" />
                    <path d="m3 7 9 6 9-6" />
                  </svg>
                </span>
                <span>
                  <span className="contactCard__label">Email</span>
                  <span className="contactCard__value" style={{ display: "block" }}>
                    eyalglraz@gmail.com
                  </span>
                </span>
              </a>
              <a
                className="contactCard"
                href="https://www.linkedin.com/in/eyal-blumental-erez-b6ba4627b/"
                target="_blank"
                rel="noreferrer"
              >
                <span className="contactCard__icon">
                  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                    <path d="M4.98 3.5C4.98 4.88 3.87 6 2.5 6S.02 4.88.02 3.5 1.13 1 2.5 1s2.48 1.12 2.48 2.5zM.5 8h4v15h-4V8zm7.5 0h3.8v2.05h.06c.53-1 1.82-2.05 3.75-2.05C19.63 8 20.5 10.6 20.5 14v9h-4v-8c0-1.9-.03-4.35-2.65-4.35-2.65 0-3.05 2.07-3.05 4.2V23H8V8z" />
                  </svg>
                </span>
                <span>
                  <span className="contactCard__label">LinkedIn</span>
                  <span className="contactCard__value" style={{ display: "block" }}>
                    Eyal Blumental Erez
                  </span>
                </span>
              </a>
              <a className="contactCard" href={CV} target="_blank" rel="noreferrer">
                <span className="contactCard__icon">
                  <svg
                    viewBox="0 0 24 24"
                    width="22"
                    height="22"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <path d="M14 2v6h6" />
                    <path d="M9 13h6M9 17h6" />
                  </svg>
                </span>
                <span>
                  <span className="contactCard__label">Curriculum Vitae</span>
                  <span className="contactCard__value" style={{ display: "block" }}>
                    View my CV
                  </span>
                </span>
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="footer">
        <div className="container footer__inner">
          <span className="footer__copy">
            © {new Date().getFullYear()} Eyal Blumental Erez — Auckland, New Zealand
          </span>
          <a className="footer__top" href="#landingPage">
            Back to top ↑
          </a>
        </div>
      </footer>
    </div>
  );
}

export default Home;
